import { useEffect } from "preact/hooks";
import PublicHeader from "../components/PublicHeader.js";
import PublicFooter from "../components/PublicFooter.js";

export default function Security() {
  useEffect(() => {
    document.title = "Security — webhookstorage";

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Security practices, encryption, data residency, tenant isolation, and compliance information for webhookstorage.",
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://webhookstorage.com/security");

    window.scrollTo(0, 0);
  }, []);

  return (
    <div class="min-h-screen bg-white text-slate-900">
      <PublicHeader />
      <div class="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <h1 class="mb-2 text-3xl font-bold tracking-tight">Security</h1>
        <p class="mb-10 text-sm text-slate-500">
          How we protect your data and maintain trust.
        </p>

        <div class="space-y-8 text-sm leading-relaxed text-slate-700">

          {/* ── Overview ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">Overview</h2>
            <p>
              webhookstorage is designed for production webhook workflows carrying sensitive data. Security is foundational to how we build and operate the service — not an afterthought.
            </p>
          </section>

          {/* ── Encryption ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">Encryption</h2>
            <ul class="ml-5 list-disc space-y-2">
              <li>
                <strong>At rest:</strong> All webhook payloads and metadata are encrypted using AES-256 via AWS Key Management Service (KMS). Encryption keys are managed by AWS and are never accessible to application code.
              </li>
              <li>
                <strong>In transit:</strong> All connections use TLS 1.2 or higher. The S3 bucket policy denies non-HTTPS requests, ensuring payloads are never transmitted in cleartext.
              </li>
            </ul>
          </section>

          {/* ── Data Residency ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">Data Residency</h2>
            <p>
              All data is stored and processed in <strong>US East (AWS us-east-1, N. Virginia)</strong>. Compute, storage, and networking infrastructure are co-located in this region. We do not replicate customer data to other regions.
            </p>
          </section>

          {/* ── Tenant Isolation ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">Tenant Isolation</h2>
            <p class="mb-3">Customer data is logically isolated at every layer:</p>
            <ul class="ml-5 list-disc space-y-2">
              <li><strong>S3:</strong> Per-tenant key prefixes ensure one customer's payloads are never co-mingled with another's.</li>
              <li><strong>DynamoDB:</strong> Per-tenant partition keys enforce data boundaries at the database level.</li>
              <li><strong>Application:</strong> Tenant identity is derived from authenticated JWTs, not user-supplied headers. Every API request is verified against the authenticated tenant before data access.</li>
            </ul>
          </section>

          {/* ── Authentication ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">Authentication</h2>
            <p class="mb-3">Three authentication models serve different use cases:</p>
            <ul class="ml-5 list-disc space-y-2">
              <li><strong>Endpoint secrets:</strong> Used by webhook senders to authenticate ingest requests. Stored as SHA-256 hashes — we cannot retrieve them, only verify them. Secrets support zero-downtime rotation with up to two active secrets per endpoint.</li>
              <li><strong>Session tokens:</strong> Issued via WorkOS (magic link or passkey). Used for dashboard access. Tokens are short-lived and automatically refreshed.</li>
              <li><strong>API keys:</strong> For programmatic access to the management API. Stored as one-way hashes. Plaintext is shown only once at creation time.</li>
              <li><strong>Notification signing:</strong> Outbound notifications are signed with Ed25519. The public key for verification is available at <code><a href="/docs#outbound" class="text-teal-700 underline hover:text-teal-900">GET /v1/signing-keys</a></code>.</li>
            </ul>
          </section>

          {/* ── Network Security ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">Network Security</h2>
            <ul class="ml-5 list-disc space-y-2">
              <li><strong>Static IPs:</strong> Dedicated Elastic IPs for both ingest and outbound delivery. These IPs do not change across deployments.</li>
              <li><strong>IP allowlists:</strong> Per-endpoint IP allowlists restrict which source IPs can send webhooks to a given endpoint.</li>
              <li><strong>Rate limiting:</strong> Per-tenant (1,000 req/min) and per-endpoint (100 req/min) rate limits protect against abuse. Responses include <code>Retry-After</code> headers.</li>
            </ul>
          </section>

          {/* ── Availability ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">Availability</h2>
            <ul class="ml-5 list-disc space-y-2">
              <li><strong>Self-serve tiers:</strong> 99.5% monthly uptime target.</li>
              <li><strong>Business tier:</strong> 99.9% monthly uptime (or as negotiated).</li>
              <li>
                <strong>Status page:</strong>{" "}
                <a href="https://status.webhookstorage.com" class="text-teal-600 hover:underline" target="_blank" rel="noopener noreferrer">
                  status.webhookstorage.com
                </a>{" "}
                — real-time status, incident history, and maintenance notifications.
              </li>
              <li>
                <strong>Continuous backups:</strong> DynamoDB Point-In-Time Recovery (PITR) is enabled, providing continuous backups with per-second granularity for the preceding 35 days.
              </li>
            </ul>
          </section>

          {/* ── Compliance ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">Compliance</h2>
            <ul class="ml-5 list-disc space-y-2">
              <li>
                <strong>Data Processing Agreement:</strong> A DPA is available at{" "}
                <a href="/legal" class="text-teal-600 hover:underline">/legal</a>{" "}
                in your account dashboard.
              </li>
              <li><strong>Subprocessor list:</strong> Documented in DPA Section 6.2. We provide 30 days advance notice for subprocessor changes.</li>
              <li>Our privacy policy and terms of service are publicly available at <a href="/privacy" class="text-teal-600 hover:underline">/privacy</a> and <a href="/terms" class="text-teal-600 hover:underline">/terms</a>.</li>
            </ul>
          </section>

          {/* ── Data Retention ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">Data Retention</h2>
            <div class="overflow-x-auto mb-4">
              <table class="w-full text-left text-sm">
                <thead>
                  <tr class="border-b border-slate-200">
                    <th class="pb-2 pr-4 font-semibold text-slate-900">Tier</th>
                    <th class="pb-2 font-semibold text-slate-900">Retention</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  <tr>
                    <td class="py-2 pr-4">Free</td>
                    <td class="py-2">1 day</td>
                  </tr>
                  <tr>
                    <td class="py-2 pr-4">Activated (pay-as-you-go)</td>
                    <td class="py-2">7 days</td>
                  </tr>
                  <tr>
                    <td class="py-2 pr-4">Business</td>
                    <td class="py-2">Custom</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="mb-3">
              Payloads are hard-deleted from S3 after the retention window expires. Associated metadata is removed within 7 days of payload deletion.
            </p>
            <p>
              On account termination, a 30-day grace period allows reactivation. After the grace period, all data is permanently and irreversibly deleted.
            </p>
          </section>

          {/* ── Security Contact ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">Security Contact</h2>
            <p>
              To report a security vulnerability or for security-related inquiries, contact us at{" "}
              <a href="mailto:security@webhookstorage.com" class="text-teal-600 hover:underline">
                security@webhookstorage.com
              </a>
              . For general support, reach us at{" "}
              <a href="mailto:support@webhookstorage.com" class="text-teal-600 hover:underline">
                support@webhookstorage.com
              </a>
              .
            </p>
          </section>

        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
